import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../config/api';
import './BookingDetailsPage.css';

const BookingDetailsPage = () => {
  const { token,code} = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loadingQr, setLoadingQr] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resolvedToken, setResolvedToken] = useState(null);

  const showError = (message) => {
    setErrorMessage(message);
    setShowErrorPopup(true);
  };



  useEffect(() => {
    const generateQRCode = () => {
      
      const bookingCode = booking?.myvistaBookingCode;
      
      // console.log('Generating QR for:', bookingCode);
      
      if (bookingCode) {
        setLoadingQr(true);
        try {
          const encodedCode = encodeURIComponent(String(bookingCode));
          const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodedCode}&margin=10`;
          
          // console.log('QR URL:', qrUrl);
          setQrCodeUrl(qrUrl);
        } catch (error) {
          console.error('Error generating QR code:', error);
        } finally {
          setLoadingQr(false);
        }
      }
    };
  
    generateQRCode();
  }, [booking?.myvistaBookingCode]);

  // useEffect(() => {
  //   const fetchBooking = async () => {
  //     if (!token) {
  //       setError('Invalid booking link');
  //       setLoading(false);
  //       return;
  //     }

  //     try {
  //       setLoading(true);
  //       setError(null);
  //       const result = await api.getBookingByViewToken(token);
        
  //       if (result.success && result.data) {
  //         setBooking(result.data);
  //       } else {
  //         setError(result.error || result.message || 'Failed to load booking details');
  //       }
  //     } catch (err) {
  //       console.error('Error fetching booking:', err);
  //       setError(err.message || 'Failed to load booking details. Please check the link or contact support.');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchBooking();
  // }, [token]);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        setError(null);
        setResolvedToken(null);
  
        let finalToken = token;
  
        // ✅ Resolve short link using API layer
        if (!finalToken && code) {
          const result = await api.getShortLink(code);
  
          if (result.success && result.data?.url) {
            finalToken = result.data.url.split('/booking/view/')[1];
          } else {
            setError('Invalid booking link');
            return;
          }
        }
  
        if (!finalToken) {
          setError('Invalid booking link');
          return;
        }

        setResolvedToken(finalToken);
  
        const result = await api.getBookingByViewToken(finalToken);
  
        if (result.success && result.data) {
          setBooking(result.data);
        } else {
          setError(result.error || result.message || 'Failed to load booking details');
        }
  
      } catch (err) {
        console.error('Error fetching booking:', err);
        setError(err.message || 'Failed to load booking details.');
      } finally {
        setLoading(false);
      }
    };
  
    fetchBooking();
  }, [token, code]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // If it's already in 12-hour format, return as is
      if (timeString.includes('PM') || timeString.includes('AM')) {
        return timeString;
      }
      // Convert 24-hour to 12-hour format
      const [hours, minutes] = timeString.split(':');
      const hour12 = parseInt(hours) % 12 || 12;
      const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes} ${ampm}`;
    } catch (e) {
      return timeString;
    }
  };

  const canCancelBooking = () => {
    if (!booking) return false;
    
    const status = (booking.bookingStatus || '').toLowerCase();
    if (status === 'cancelled' || status === 'completed') {
      return false;
    }

    // Check if show time has passed
    if (booking.showtime?.date && booking.showtime?.time) {
      try {
        const showDateTime = new Date(`${booking.showtime.date}T${booking.showtime.time}`);
        const now = new Date();
        if (showDateTime < now) {
          return false;
        }
      } catch (e) {
        // If date parsing fails, allow cancellation attempt (backend will handle validation)
      }
    }

    return true;
  };

  const handleCancelBooking = async () => {
    if (!booking) return;

    const bookingId = booking.bookingId || booking.id;
    if (!bookingId) return;

    try {
      setCancelling(true);
      const result = await api.refundBooking(bookingId, {
        cancellationReason: 'Customer cancelled',
        cancelledBy: 'customer',
      });

      if (result.success) {
        showError(
          (result.data && result.data.message) ||
            result.message ||
            'Booking cancelled successfully',
        );
        const refreshResult = await api.getBookingByViewToken(resolvedToken);
        if (refreshResult.success && refreshResult.data) {
          setBooking(refreshResult.data);
        }
      } else {
        throw new Error(result.error || result.message || 'Failed to cancel booking');
      }
    } catch (err) {
      console.error('Error cancelling booking:', err);
      showError(err.message || 'Failed to cancel booking. Please try again or contact support.');
    } finally {
      setCancelling(false);
      setShowCancelConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="booking-details-page">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading booking details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="booking-details-page">
        <div className="container">
          <div className="error-container">
            <div className="error-icon">⚠️</div>
            <h2>Booking Not Found</h2>
            <p>{error || 'Invalid or expired booking link. Please check the link or contact support.'}</p>
            <button className="primary-btn" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  const bookingStatus = (booking.bookingStatus || '').toLowerCase();
  const isCancelled = bookingStatus === 'cancelled';
  const isCompleted = bookingStatus === 'completed';

  return (
    <div className="booking-details-page">
      <div className="container">
        <div className="booking-details-card">
          {/* Header */}
          <div className="booking-header">
            <div className="header-content">
              <h1>Booking Details</h1>
              <div className={`status-badge ${bookingStatus}`}>
                {bookingStatus === 'confirmed' ? 'Confirmed' : 
                 bookingStatus === 'pending' ? 'Pending' :
                 bookingStatus === 'cancelled' ? 'Cancelled' :
                 bookingStatus === 'completed' ? 'Completed' : 'Unknown'}
              </div>
            </div>
          </div>

          {/* Booking Information */}
          <div className="booking-info-section">
            <h2>Booking Information</h2>
            
            <div className="detail-row">
              <span className="detail-label">Booking ID:</span>
              <span className="detail-value">
                {booking.myvistaBookingCode }
              </span>
            </div>

            {booking.customerName && (
              <div className="detail-row">
                <span className="detail-label">Customer Name:</span>
                <span className="detail-value">{booking.customerName}</span>
              </div>
            )}

            {booking.customerEmail && (
              <div className="detail-row">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{booking.customerEmail}</span>
              </div>
            )}

            {booking.customerPhone && (
              <div className="detail-row">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{booking.customerPhone}</span>
              </div>
            )}
          </div>

          {/* Movie Information */}
          {booking.movie && (
            <div className="booking-info-section">
              <h2>Movie Information</h2>
              
              {booking.movie.title && (
                <div className="detail-row">
                  <span className="detail-label">Movie:</span>
                  <span className="detail-value">{booking.movie.title}</span>
                </div>
              )}

              {booking.movie.rating && (
                <div className="detail-row">
                  <span className="detail-label">Rating:</span>
                  <span className="detail-value">{booking.movie.rating}</span>
                </div>
              )}
            </div>
          )}

          {/* Show Information */}
          {booking.showtime && (
            <div className="booking-info-section">
              <h2>Show Information</h2>
              
              {booking.cinema && booking.cinema.name && (
                <div className="detail-row">
                  <span className="detail-label">Cinema:</span>
                  <span className="detail-value">{booking.cinema.name}</span>
                </div>
              )}

              {booking.cinema && booking.cinema.address && (
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{booking.cinema.address}</span>
                </div>
              )}

              {booking.showtime.screenName && (
                <div className="detail-row">
                  <span className="detail-label">Screen:</span>
                  <span className="detail-value">{booking.showtime.screenName}</span>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Date:</span>
                <span className="detail-value">{formatDate(booking.showtime.date)}</span>
              </div>

              <div className="detail-row">
                <span className="detail-label">Time:</span>
                <span className="detail-value">{formatTime(booking.showtime.time)}</span>
              </div>
            </div>
          )}

          {/* Seats */}
          {booking.seats && booking.seats.length > 0 && (
            <div className="booking-info-section">
              <h2>Seats</h2>
              {(() => {
                // Group seats by category for BMS-style display.
                const labelFor = (seat) => {
                  const raw =
                    seat?.ticketTypeDescription ||
                    seat?.areaName ||
                    (seat?.category
                      ? String(seat.category).charAt(0).toUpperCase() +
                        String(seat.category).slice(1)
                      : null);
                  return raw ? String(raw).toUpperCase() : null;
                };

                const numberFor = (seat) =>
                  seat?.displayNumber ||
                  `${seat?.row || ''}${seat?.number || ''}` ||
                  seat?.id ||
                  seat ||
                  '';

                // seatsString (when present from server) bypasses grouping.
                if (booking.seatsString) {
                  return (
                    <div className="detail-row">
                      <span className="detail-label">Selected Seats:</span>
                      <span className="detail-value">{booking.seatsString}</span>
                    </div>
                  );
                }

                const groupMap = new Map();
                booking.seats.forEach((seat) => {
                  const label = labelFor(seat) || 'SEAT';
                  if (!groupMap.has(label)) groupMap.set(label, []);
                  const num = numberFor(seat);
                  if (num) groupMap.get(label).push(num);
                });
                const seatGroups = Array.from(groupMap.entries()).map(
                  ([categoryLabel, seats]) => ({ categoryLabel, seats }),
                );

                return (
                  <>
                    <div className="detail-row">
                      <span className="detail-label">Tickets:</span>
                      <span className="detail-value">
                        <span className="ticket-count-badge">
                          {booking.seats.length} Ticket
                          {booking.seats.length > 1 ? 's' : ''}
                        </span>
                      </span>
                    </div>
                    <div className="detail-row detail-row-seats">
                      <span className="detail-label">Selected Seats:</span>
                      <span className="detail-value detail-value-seats">
                        {seatGroups.map((group, idx) => (
                          <span key={idx} className="seat-group">
                            {group.categoryLabel && (
                              <span className="seat-group-label">
                                {group.categoryLabel}
                              </span>
                            )}
                            {group.categoryLabel && ' - '}
                            {group.seats.join(', ')}
                            {idx < seatGroups.length - 1 && (
                              <span className="seat-group-sep"> • </span>
                            )}
                          </span>
                        ))}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

{qrCodeUrl && (
  <div className="booking-info-section">
    <h2>QR Code (Booking Verification)</h2>
    {loadingQr ? (
      <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
        Generating QR code...
      </div>
    ) : (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '15px',
        padding: '20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
      }}>
        <img
          src={qrCodeUrl}
          alt="Booking QR Code"
          style={{
            width: '250px',
            height: '250px',
            border: '3px solid #dee2e6',
            borderRadius: '4px',
            objectFit: 'contain',
            backgroundColor: '#fff',
            padding: '10px',
          }}
          onError={() => console.error('Failed to load QR code image')}
        />
        <div style={{ textAlign: 'center' }}>
          <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '13px', fontWeight: '500' }}>
            Scan at cinema for verification
          </p>
          <p style={{ margin: '4px 0', color: '#999', fontSize: '12px' }}>
            Booking ID: <strong>{booking?.myvistaBookingCode}</strong>
          </p>
        </div>
      </div>
    )}
  </div>
)}

          {/* Pricing */}
          {booking.pricing && (
            <div className="booking-info-section">
              <h2>Pricing</h2>
              
              {booking.pricing.ticketPrice > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Ticket Price:</span>
                  <span className="detail-value">₹{booking.pricing.ticketPrice.toFixed(2)}</span>
                </div>
              )}

              {booking.pricing.snacksAmount > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Snacks:</span>
                  <span className="detail-value">₹{booking.pricing.snacksAmount.toFixed(2)}</span>
                </div>
              )}

              {Array.isArray(booking.promotionalItems) && booking.promotionalItems.length > 0 && (
                <div className="detail-row" style={{ color: '#28a745', fontWeight: 500 }}>
                  <span className="detail-label">
                    🎁 {booking.promotionalItems[0].name} (Website Launch Offer):
                  </span>
                  <span className="detail-value">FREE</span>
                </div>
              )}

              {booking.pricing.convenienceFeeBase > 0 && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">Convenience Fee:</span>
                    <span className="detail-value">₹{(booking.pricing.convenienceFeeBase + (booking.pricing.convenienceFeeGST || 0)).toFixed(2)}</span>
                  </div>
                  {booking.pricing.convenienceFeeBase > 0 && (
                    <div className="detail-row" style={{ paddingLeft: '20px', fontSize: '0.9em', color: '#6c757d' }}>
                      <span className="detail-label">Base Amount:</span>
                      <span className="detail-value">₹{booking.pricing.convenienceFeeBase.toFixed(2)}</span>
                    </div>
                  )}
                  {booking.pricing.convenienceFeeGST > 0 && (
                    <div className="detail-row" style={{ paddingLeft: '20px', fontSize: '0.9em', color: '#6c757d' }}>
                      <span className="detail-label">Integrated GST (IGST) @ 18%:</span>
                      <span className="detail-value">₹{booking.pricing.convenienceFeeGST.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}

              {booking.pricing.couponDiscount > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Coupon Discount:</span>
                  <span className="detail-value">-₹{booking.pricing.couponDiscount.toFixed(2)}</span>
                </div>
              )}

              <div className="detail-row highlight">
                <span className="detail-label">Total Amount:</span>
                <span className="detail-value">₹{booking.pricing.totalAmount.toFixed(2)}</span>
              </div>
            </div>
          )}

          {/* Payment Information */}
          {booking.payment && (
            <div className="booking-info-section">
              <h2>Payment Information</h2>
              
              <div className="detail-row">
                <span className="detail-label">Payment Status:</span>
                <span className="detail-value">
                  {booking.payment.status === 'completed' ? 'Paid' :
                   booking.payment.status === 'pending' ? 'Pending' :
                   booking.payment.status === 'authorized' ? 'Authorized' :
                   booking.payment.status || 'N/A'}
                </span>
              </div>

              {booking.paymentMethod && (
                <div className="detail-row">
                  <span className="detail-label">Payment Method:</span>
                  <span className="detail-value">
                    {String(booking.paymentMethod).toUpperCase()}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Actions */}
          <div className="booking-actions">
            {canCancelBooking() && !isCancelled && !isCompleted && (
              <button 
                className="cancel-btn" 
                onClick={() => setShowCancelConfirm(true)}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Booking'}
              </button>
            )}
            
            <button className="primary-btn" onClick={() => navigate('/')}>
              Go to Home
            </button>
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
          <div className="modal-overlay" onClick={() => setShowCancelConfirm(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h3>Cancel Booking</h3>
              <p>Are you sure you want to cancel this booking? This action cannot be undone.</p>
              <div className="modal-actions">
                <button 
                  className="secondary-btn" 
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancelling}
                >
                  No, Keep Booking
                </button>
                <button 
                  className="cancel-btn" 
                  onClick={handleCancelBooking}
                  disabled={cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel Booking'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showErrorPopup && (
          <div className="error-popup-overlay" onClick={() => setShowErrorPopup(false)}>
            <div className="error-popup-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-popup-icon">
                <i className="fas fa-exclamation-circle"></i>
              </div>
              <div className="error-popup-message">
                {errorMessage}
              </div>
              <button
                className="error-popup-button"
                onClick={() => setShowErrorPopup(false)}
              >
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetailsPage;

